// AuthForm Component
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/utils/api";

const AuthForm = ({ mode, onSuccess, onGoogleLogin, onSwitchMode }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [dob, setDob] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
      const data = mode === "login" ? { email, password } : { email, password, name, dob };
      const response = await api.post(endpoint, data);
      localStorage.setItem("token", response.data.token);
      toast.success(mode === "login" ? "Welcome back!" : "Account created!");
      onSuccess(response.data.user, response.data.token);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <Button 
        variant="outline" 
        className="w-full gap-2 rounded-full"
        onClick={onGoogleLogin}
        data-testid="google-login-btn"
      >
        <svg className="h-5 w-5" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        Continue with Google
      </Button>

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-background px-2 text-muted-foreground">Or</span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {mode === "register" && (
          <>
            <div>
              <Label>Name</Label>
              <Input 
                value={name} 
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                required
                data-testid="name-input"
              />
            </div>
            <div>
              <Label>Date of Birth <span className="text-red-500">*</span></Label>
              <Input 
                type="date"
                value={dob} 
                onChange={(e) => setDob(e.target.value)}
                required
                max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                data-testid="dob-input"
              />
              <p className="text-xs text-muted-foreground mt-1">You must be 18 or older to register</p>
            </div>
          </>
        )}
        <div>
          <Label>Email</Label>
          <Input 
            type="email"
            value={email} 
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
            data-testid="email-input"
          />
        </div>
        <div>
          <Label>Password</Label>
          <Input 
            type="password"
            value={password} 
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            required
            data-testid="password-input"
          />
        </div>
        <Button type="submit" className="w-full rounded-full bg-emerald-500 hover:bg-emerald-600" disabled={loading} data-testid="submit-auth-btn">
          {loading ? "Loading..." : mode === "login" ? "Sign In" : "Create Account"}
        </Button>
      </form>

      <p className="text-center text-sm text-muted-foreground">
        {mode === "login" ? "Don't have an account? " : "Already have an account? "}
        <button onClick={onSwitchMode} className="text-emerald-600 hover:underline" data-testid="switch-auth-mode">
          {mode === "login" ? "Sign up" : "Sign in"}
        </button>
      </p>
    </div>
  );
};

export default AuthForm;
